import {DemandSourceRecord, IncludeExclude, PageName, PageState, ProductsAndCampaigns, PublisherOrSiteOption, ResponseStatus, SortState, TrafficSourceResponseDto, User, UserListResponseDto, UserState } from "./types";
import dayjs from "./dayjs";

// Helper function to create default page state
const createDefaultPageState = (page: string | null = null): PageState => ({
  page: page,
  dateStart: dayjs.utc(),
  dateEnd: dayjs.utc(),
  publisherIds: [],
  siteIds: [],
  placementIds: [],
  trafficSourcesIncludeExclude: IncludeExclude.Include,
  placementsIncludeExclude: IncludeExclude.Include,
  trafficTypes: null,
  trafficTypesIncludeExclude: IncludeExclude.Include,
  countries : null,
  countriesIncludeExclude: IncludeExclude.Include,
  groupBy: null,
  productIds: null,
  productsIncludeExclude: IncludeExclude.Include,
  campaignIds: null,
  campaignIdsIncludeExclude: IncludeExclude.Include,
  demandSourceIds: null,
  demandSourceIdsIncludeExclude: IncludeExclude.Include,
  queryFlags: null,
  cLinks: null,
  cLinksIncludeExclude: IncludeExclude.Include,
  viewAs: null,
  pivotBy: null,
  dateMode: "days",
  performanceStatsViewMode: null,
});

// Separate state object for each page
const pageStates: { [page: string]: PageState } = {};

// Track current page
let currentPage: string | null = null;

let userState : UserState = {
  accountCreated: null,
  accountRefreshed: null,
  accountUpdated: null,
  actualUser: null,
  clinks: null,
  email: null,
  filterFields: null,
  groupByFields: null,
  name: null,
  permissions: null,
  authUserPermissions: null,
  picture: null,
  userId: null,
  userIdReference: null
}

const productsAndCampaigns : ProductsAndCampaigns = {
  dto: null
};
let demandSources: DemandSourceRecord[] = [];
const sortState : SortState = {
  columnIdSorted: "",
  sort: 0
}
let assignableUsersState:User[] = [];
let userListResponseDto:UserListResponseDto = null;
let mostFrequentTagsState:string[] = [];
let trafficSourcesDtoState:TrafficSourceResponseDto = {
  status : ResponseStatus.OK, 
  error : null,
  publishersArray : []
};

let overallSearchFuntion: Function = null;
let productsSearchFuntion: Function = null;
let performanceStatsFunction: Function = null;
let connexityPrSearchFuntion: Function = null;

const StateHelper = {
  getPageState: function (page?: string): PageState {
    // Use provided page, or currentPage, or default to products
    const targetPage = page || currentPage || PageName.products;

    // Initialize page state if it doesn't exist
    if (!pageStates[targetPage]) {
      pageStates[targetPage] = createDefaultPageState(targetPage);
    }

    // Only update currentPage tracker when getting the default current page (no explicit page parameter)
    // This prevents side effects when building menu URLs for multiple pages
    if (!page) {
      currentPage = targetPage;
    }

    return pageStates[targetPage];
  },

  setCurrentPage: function (page: string): void {
    currentPage = page;
  },

  getCurrentPage: function (): string | null {
    return currentPage;
  },

  hasPageState: function (page: string): boolean {
    return pageStates.hasOwnProperty(page);
  },

  getUserState: function(): UserState {
    return userState;
  },
  setUserState: function(newUserState: UserState) {
    userState = newUserState;
  },

  getSortState: function(): SortState {
    return sortState;
  },

  setSearchFuntion: function (page: string, funtionX: Function): void {
    if (page === PageName.overall) {
      overallSearchFuntion = funtionX;
    } else if (page === PageName.products) {
      productsSearchFuntion = funtionX;
    } else if (page === PageName.performanceStats) {
      performanceStatsFunction = funtionX;
    } else if (page === PageName.performanceReports) {
      connexityPrSearchFuntion = funtionX;
    }
  },

  getSearchFuntion: function (page: string): Function {
    let searchFunction: Function = null;
    if (page === PageName.overall) {
      searchFunction = overallSearchFuntion;
    } else if (page === PageName.products) {
      searchFunction = productsSearchFuntion;
    } else if (page === PageName.performanceStats) {
      searchFunction = performanceStatsFunction;
    } else if (page === PageName.performanceReports) {
      searchFunction = connexityPrSearchFuntion;
    }
    return searchFunction;
  },

  getProductsAndCampaignsState: function (page: string): ProductsAndCampaigns|null {
    // Return shared state for all pages that need products/campaigns data
    if (page === PageName.products || page === PageName.performanceReports) {
      return productsAndCampaigns;
    }
    return null;
  },

  getDemandSourcesState: function() {
    return demandSources;
  },
  setDemandSourcesState: function(newDemandSources:DemandSourceRecord[]):void {
    // Ensure we never set undefined or null - fallback to empty array
    demandSources = newDemandSources && Array.isArray(newDemandSources) ? newDemandSources : [];
  },

  getUserListResponseDto: function(): UserListResponseDto {
    return userListResponseDto;
  },
  setUserListResponseDto: function(newUserListResponseDto:UserListResponseDto): void {
    userListResponseDto = newUserListResponseDto;
  },

  getAssignableUsersState: function(): User[] {
    return assignableUsersState;
  },
  setAssignableUsersState: function(newAssignableUsers:User[]): void {
    assignableUsersState = newAssignableUsers;
  },

  getMostFrequentTagsState: function(): string[] {
    return mostFrequentTagsState;
  },
  setMostFrequentTagsState: function(newMostFrequentTags:string[]): void {
    mostFrequentTagsState = newMostFrequentTags;
  },

  getTrafficSourcesDtoState: function(): TrafficSourceResponseDto {
    return trafficSourcesDtoState;
  },
  setTrafficSourcesDtoState: function(newTrafficSourcesDtoState:TrafficSourceResponseDto): void {
    trafficSourcesDtoState = newTrafficSourcesDtoState;
  },
  
  getTrafficSourcesArray: function(): PublisherOrSiteOption[] | null {
    return trafficSourcesDtoState && trafficSourcesDtoState.publishersArray 
      ? trafficSourcesDtoState.publishersArray 
      : null;
  },

  getLocalStorageItem: function(key:string): string {
    return window.localStorage.getItem(key);
  },
  setLocalStorageItem: function(key:string, value:string) {
    window.localStorage.setItem(key, value);    
  }
};

export default StateHelper;